import { stripAgentInvisibleMarkup } from "../shifty/shared/markdownSanitization";
import {
  looksLikeWriteProseJsonEnvelope,
  unwrapWriteProseFromPossiblyStructuredRaw,
} from "./unwrapWriteProseJsonEnvelope";

function paragraphLooksLikeTrailingAgentRationale(paragraph: string): boolean {
  const p = String(paragraph ?? "").trim();
  return /^I (?:kept|preserved|maintained|retained)\s+(?:your|the)\s+(?:structure|voice|tone|pacing|meaning|beats)\b/i.test(p);
}

const ILL_EXPAND = /\bI'?ll expand\b/i;
const GROUND_IN_MORE_SPECIFIC = /\bground(?:s|ing)? (?:the |this )?\w+.*\b(?:in|with) more specific\b/i;
const AGENT_EDITORIAL_CHATTER_RE = new RegExp(
  [
    String.raw`\bhere(?:'|’)?s a revised version\b`,
    String.raw`\bhere(?:'|’)?s (?:the |two ways to )?expand(?:ed)?\b`,
    String.raw`\bhere are (?:two|three|four|five|\d+) ways to expand\b`,
    String.raw`\b(?:generated|developed) with muse\b`,
    String.raw`\bhere(?:'|’)?s (?:a|an) (?:tighter|shorter|cleaner|stronger|sharper|revised)\s+(?:version|rewrite|pass|draft)\b`,
    String.raw`\bhere is (?:a|an) (?:tighter|shorter|cleaner|stronger|sharper|revised)\s+(?:version|rewrite|pass|draft)\b`,
    String.raw`^\*{0,2}changes made\*{0,2}\s*:`,
    String.raw`\bchanges made\s*:`,
    String.raw`^\*{0,2}what changed\*{0,2}\s*:`,
    String.raw`\bwhat changed\s*:`,
    String.raw`^\*{0,2}what i \w[\w'-]*\*{0,2}\s*:`,
    String.raw`\bwhat i cut\s*:`,
    String.raw`\b(?:this|that)\s+cuts(?:\s+it)?\s+from\s+\d+\s+words?\s+to\s+\d+(?:\s+words?)?\b`,
    String.raw`\b(?:cut|reduced|shortened)\s+from\s+\d+\s+words?\s+to\s+\d+(?:\s+words?)?\b`,
    String.raw`\b(?:this|that)\s+cuts(?:\s+about|\s+roughly|\s+around)?\s+\d+\s*%`,
    String.raw`\bcuts(?:\s+it)?\s+about\s+\d+\s*%`,
    String.raw`\bkeeping all the action beats\b`,
    String.raw`\b(?:now )?stand(?:s)? alone for impact\b`,
    String.raw`\bphysical details stay intact\b`,
    String.raw`\bremoved filter words\b`,
    String.raw`\bkeeping the (?:sensory|tactical|essential|same)\b`,
    String.raw`\bthe original (?:uses|has|said|reads|was)\b`,
    String.raw`\b(?:slightly|a bit|kind of) redundant\b`,
    String.raw`\bfeels a bit abstract\b`,
    String.raw`\bthese alternatives\b`,
    String.raw`\b(?:this|that) (?:version|rewrite|alternative|option|draft)\b`,
    String.raw`\beach version maintains\b`,
    String.raw`\bwhich version do you prefer\b`,
    String.raw`\bwant me to (?:insert|replace)\b`,
    String.raw`\bwould you like (?:me to|to see)\b`,
    String.raw`\bor would you like\b`,
    String.raw`\bother variations\b`,
    String.raw`\bhere are (?:\d+|three|four|five|several) (?:options?|variations?|versions?)\b`,
  ].join("|"),
  "i",
);

/** Agent commentary / follow-up questions that must never be inserted into manuscript body. */
export function paragraphLooksLikeAgentEditorialChatter(paragraph: string): boolean {
  const p = String(paragraph ?? "").trim();
  if (!p) return false;

  // Artifact cleanup only: a standalone UI follow-up is not manuscript prose.
  if (/^Should I insert it into your document\?$/i.test(p)) return true;
  if (ILL_EXPAND.test(p) && p.length < 220) return true;
  return AGENT_EDITORIAL_CHATTER_RE.test(p) || GROUND_IN_MORE_SPECIFIC.test(p);
}

/** Trim surrounding whitespace without removing a leading task item's nesting. */
export function trimPreservingTaskIndent(text: string): string {
  const body = String(text ?? "").replace(/^(?:[ \t]*\r?\n)+/, "");
  return /^[ \t]*[-*] +\[[ xX]\](?:\s|$)/.test(body) ? body.trimEnd() : body.trim();
}

/** Drop agent editorial / consent paragraphs; keep only manuscript prose blocks. */
export function stripAgentEditorialChatterParagraphsForInsertion(text: string): string {
  const trimmed = trimPreservingTaskIndent(text);
  if (!trimmed) return trimmed;

  const paragraphs = trimmed.split(/\n\s*\n/).map((part) => (
    // Task-item indentation is list structure, including after a blank line.
    /^[ \t]*[-*] +\[[ xX]\](?:\s|$)/.test(part) ? part.trimEnd() : part.trim()
  )).filter(Boolean);
  if (paragraphs.length <= 1) return trimmed;

  const kept = paragraphs.filter(
    (part) => !paragraphLooksLikeAgentEditorialChatter(part) && !/^-{3,}$/.test(part),
  );
  if (kept.length === 0) return paragraphs[0] ?? trimmed;
  return trimPreservingTaskIndent(kept.join("\n\n"));
}

/** Remove trailing multi-option follow-up chatter that must never land in manuscript body edits. */
export function stripTrailingWriteOptionFollowUpChatter(text: string): string {
  let trimmed = String(text ?? "").trim();
  if (!trimmed) return trimmed;

  const paragraphs = trimmed.split(/\n\s*\n/);
  while (paragraphs.length > 1) {
    const last = paragraphs[paragraphs.length - 1]!.trim();
    if (
      paragraphLooksLikeAgentEditorialChatter(last)
      || paragraphLooksLikeTrailingAgentRationale(last)
      || /want me to insert (?:one of )?these\b/i.test(last)
      || /want me to replace\b/i.test(last)
      || /would you like me to generate more variants/i.test(last)
      || /would you like to see other variations/i.test(last)
      || /which (?:one |version )?would you prefer/i.test(last)
      || /let me know which (?:one|option)\b/i.test(last)
    ) {
      paragraphs.pop();
      trimmed = paragraphs.join("\n\n").trim();
    } else {
      break;
    }
  }
  return stripAgentEditorialChatterParagraphsForInsertion(trimmed);
}

/** Sanitize one carousel suggestion body for display, apply, and insert binding. */
export function sanitizeWriteCarouselSuggestionBody(raw: string): string {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return "";
  // Write-button structured output sometimes lands in suggestions[] as a JSON envelope.
  const unwrapped = unwrapWriteProseFromPossiblyStructuredRaw(trimmed, 1);
  const source = unwrapped
    ?? (looksLikeWriteProseJsonEnvelope(trimmed) ? "" : trimmed);
  return stripAgentInvisibleMarkup(stripTrailingWriteOptionFollowUpChatter(source));
}

const CAROUSEL_CRAFT_INSTRUCTION_PHRASES = [
  "show rather than tell",
  "physical sensation",
  "external trigger",
  "warrior voice",
  "not abstraction",
  "speak in terms of",
  "turning reassurance",
  "let the weight land",
  "simple declarations",
  "simple declaration",
  "battle, not abstraction",
  "craft instruction",
  "call to arms",
  "action beat",
];

const CAROUSEL_IMPERATIVE_ADVICE_START =
  /^(?:cut|use|let|challenge|add|remove|keep|sharpen|ground|show|turn|try|consider|avoid|lean|deepen|tighten|expand|open|close|start|end|drop|raise|bring|make)\b/i;

/** True when text reads like on-page manuscript prose rather than rewrite coaching. */
export function carouselSuggestionLooksLikeManuscriptProse(suggestion: string): boolean {
  const s = String(suggestion ?? "").trim();
  if (!s) return false;
  if (/["'“”‘’]/.test(s)) return true;
  if (
    /\b(?:she|he|they|[A-Z][a-z]{2,})\s+(?:was|were|is|are|had|has|felt|said|looked|turned|stepped|pressed|swallowed|drew|stood|watched|knew|saw|heard|whispered|muttered|replied|asked|breathed|moved|waited|forgot)\b/.test(
      s,
    )
  ) {
    return true;
  }
  if (/\bwhat if (?:she|he|they)\b/i.test(s)) return true;
  if (/^let (?:her|him|them|me|us)\s+\w+/i.test(s)) return true;
  if (/\b(?:she|he|they|[A-Z][a-z]{2,})\s+(?:\w+ed|\w+ing)\b/.test(s) && s.length >= 24) return true;
  if (
    s.length >= 72
    && /\b(?:the|her|his|their)\s+\w+/i.test(s)
    && !CAROUSEL_IMPERATIVE_ADVICE_START.test(s)
  ) {
    return true;
  }
  return false;
}

import { suggestionHasCritiqueCarouselMarkers, suggestionLooksLikeDropInRewrite, suggestionLooksLikeEditDirectiveCluster } from "./writeCarouselDropInClassifier";

/** Craft instructions / meta summaries that must never appear as carousel apply targets. */
export function carouselSuggestionLooksLikeWritingAdvice(
  suggestion: string,
  sourcePassage?: string,
): boolean {
  const s = String(suggestion ?? "").trim();
  if (!s) return true;
  if (paragraphLooksLikeAgentEditorialChatter(s)) return true;
  // Hard critique chrome (assessment / market-meta / allow-edits CTA) — even when long.
  if (suggestionHasCritiqueCarouselMarkers(s)) return true;
  // Multi-line edit-audit clusters ("Sharpen… / Audit… / Eliminate…") are not drop-ins.
  if (suggestionLooksLikeEditDirectiveCluster(s)) return true;
  // Explicit coaching can contain narrative fragments such as "she felt".
  // Match the instruction prefix before prose heuristics; quoted dialogue does not match.
  const explicitCoachingPatterns = [
    /^use (?:an?|the)?\s*(?:external\s+)?(?:trigger|device|technique|approach|beat|physical|a)\b/i,
    /^ground (?:the|this)\s+(?:emotion|feeling|moment|scene)\b/i,
    /^try (?:using|adding|showing|grounding)\b/i,
    /^consider (?:using|adding|grounding)\b/i,
    /^show (?:rather than|the emotion|don't tell)\b/i,
    // Consume phrasal particles ("cut down on") before the opening craft object.
    // Allow modifiers in that object, but stop at pronouns, clause
    // boundaries, and punctuation instead of finding craft words in later prose.
    new RegExp(
      `${CAROUSEL_IMPERATIVE_ADVICE_START.source}(?:[ \\t]+(?:down|back)[ \\t]+on)?[ \\t]+(?:(?:an?|the|this|that|your)[ \\t]+)?`
        + "(?:(?!(?:her|him|them|me|us|she|he|they|it|we|you|i|his|their|my|our|before|after|when|while|as|if|because|until|since|and|but|or|so|that|which|who|with|without|in|on|to|of|for|from|around|through)\\b)[a-z-]+[ \\t]+)*"
        + "(?:tone|style|approach|technique|metaphor|hedging|abstraction)\\b",
      "i",
    ),
  ];
  if (explicitCoachingPatterns.some((pattern) => pattern.test(s))) return true;
  // Narrative / manuscript replacement prose is drop-in, not advice.
  if (carouselSuggestionLooksLikeManuscriptProse(s)) return false;
  // Remaining short craft-directive lines.
  if (!suggestionLooksLikeDropInRewrite(s)) return true;

  const lower = s.toLowerCase();
  const imperativeAdvicePatterns = [
    /^let (?:the|a|an)\s+(?!her\b|him\b|them\b|me\b|us\b|she\b|he\b|they\b)\S+/i,
    /^challenge (?:her|him|them|the)\b/i,
    /^cut (?:the|this)\b/i,
    /\bwithout summary\b/i,
    /\bmaking it interpersonal\b/i,
    /\bpresent-moment contrast\b/i,
    /\bphysical sensation\b/i,
    /\bto spark the memory\b/i,
    /\bdraw out (?:her|his|their) distraction\b/i,
    /\bends? cleanly on\b/i,
    /\bbridges? to chapter\b/i,
    /\bground the emotion\b/i,
    /\bnot abstraction\b/i,
    /\bspeak in terms of\b/i,
    /\blet the weight land\b/i,
    /\bturning\b.+\binto\b/i,
  ];
  if (imperativeAdvicePatterns.some((pattern) => pattern.test(s) || pattern.test(lower))) {
    return true;
  }

  if (
    CAROUSEL_CRAFT_INSTRUCTION_PHRASES.some((phrase) => lower.includes(phrase))
    && !carouselSuggestionLooksLikeManuscriptProse(s)
  ) {
    return true;
  }

  const sourceLen = String(sourcePassage ?? "").trim().length;
  if (sourceLen >= 40 && s.length < Math.min(80, sourceLen * 0.4) && !carouselSuggestionLooksLikeManuscriptProse(s)) {
    return true;
  }

  if (
    s.length < 120
    && /\bvoice\b/i.test(lower)
    && !carouselSuggestionLooksLikeManuscriptProse(s)
    && (CAROUSEL_IMPERATIVE_ADVICE_START.test(s) || !/\b(?:she|he|they)\b/i.test(s))
  ) {
    return true;
  }

  return false;
}

export function filterDropInCarouselSuggestions(
  suggestions: string[],
  sourcePassage?: string,
): string[] {
  return suggestions
    .map((entry) => sanitizeWriteCarouselSuggestionBody(entry))
    .filter(
      (entry) =>
        entry.trim().length > 0
        && !carouselSuggestionLooksLikeWritingAdvice(entry, sourcePassage),
    );
}
